import { Application, Bounds, Container, Graphics, Point, Rectangle, Sprite } from 'pixi.js';
import { CANVAS_LABEL } from '@/constants';
import { getElementsBox, type Box } from '@/utils';

type Callback = () => void;

type LineStopType = { vertical: number[]; horizontal: number[] };

export class PixiGuideLine {
  private readonly GUIDELINE_OFFSET = 5;
  private guideLayer: Graphics | null;
  private elementContainer: Container | null;

  private staticLineStops: { vertical: number[]; horizontal: number[] } | null = null;
  private cachedStops: { vertical: number[]; horizontal: number[] } | null = null;
  private checkLabel: string;

  public onChange: Callback | null = null;

  constructor() {
    this.elementContainer = null;
    this.guideLayer = new Graphics({ label: CANVAS_LABEL.GUIDE_LINE, eventMode: 'none' });
    this.checkLabel = '';
  }

  /**
   * 获取会出现辅助线的所有位置，包括画布边界，中点，和与其他元素的对比
   * @param skipMaterial 当前拖动的元素
   * @param materialName 要对比的其他元素的name值（css相同的匹配方式）
   * @returns 所有会出现辅助线的位置
   */
  private getLineGuideStops(skipElements: Container[], label: string): LineStopType {
    // 忽略其他元素的舞台中会出现辅助线的位置

    const vertical: Array<any> = this.staticLineStops?.vertical || [];
    const horizontal: Array<any> = this.staticLineStops?.horizontal || [];

    // 匹配以label开头
    const reg = new RegExp(`^${label}`);

    this.elementContainer?.getChildrenByLabel(reg).forEach((guideItem) => {
      if (skipElements.includes(guideItem)) {
        return;
      }

      //获取的全局位置，即（0，0）是左上角
      const bounds = guideItem.getBounds();
      // stage整体偏移了宽高的一半，让（0，0）为中点，
      // 所以要做转换，让元素的全局位置换算为在stage上的位置
      // 注意：需要同时转换左上角和右下角，确保宽高也从世界空间转入本地空间
      let boxX: number;
      let boxY: number;
      let boxW: number;
      let boxH: number;

      if (this.elementContainer) {
        const topLeft = this.elementContainer.toLocal(new Point(bounds.x, bounds.y));
        const bottomRight = this.elementContainer.toLocal(
          new Point(bounds.x + bounds.width, bounds.y + bounds.height)
        );
        boxX = topLeft.x;
        boxY = topLeft.y;
        boxW = bottomRight.x - topLeft.x;
        boxH = bottomRight.y - topLeft.y;
      } else {
        boxX = bounds.x;
        boxY = bounds.y;
        boxW = bounds.width;
        boxH = bounds.height;
      }

      vertical.push([boxX, boxX + boxW, boxX + boxW / 2]);
      horizontal.push([boxY, boxY + boxH, boxY + boxH / 2]);
    });

    return {
      vertical: vertical.flat(),
      horizontal: horizontal.flat()
    };
  }

  /**
   * 被拖拽的元素中拿来对比的矩形区域（区域四条边界，以及水平和垂直的两条中线）
   * @param node 拖动的元素
   * @returns 目标元素的所有对比位置
   */
  private getObjectSnappingEdges(box: Box) {
    //获取的全局位置，即（0，0）是左上角
    // const box = node.getBounds();

    //元素都是（0,0）为中点，所以offset的值center为0，其他两边各位宽高的一半
    //注意：offset值，和guide的值相加才是元素的element，而不element偏移offset的值，所以start是整数，end是负数
    return {
      vertical: [
        { guide: box.x, offset: box.width / 2, snap: 'start' },
        {
          guide: box.x + box.width / 2,
          offset: 0,
          snap: 'center'
        },
        { guide: box.x + box.width, offset: -box.width / 2, snap: 'end' }
      ],
      horizontal: [
        { guide: box.y, offset: box.height / 2, snap: 'start' },
        {
          guide: box.y + box.height / 2,
          offset: 0,
          snap: 'center'
        },
        { guide: box.y + box.height, offset: -box.height / 2, snap: 'end' }
      ]
    };
  }

  private getBox(elements: Container[]): Box {
    const box = getElementsBox(...elements);

    // getBounds() 返回的 x/y/width/height 都是世界坐标（含所有祖先 transform 叠加）。
    // 必须同时转换左上角和右下角，才能得到 elementContainer 本地空间下正确的宽高；
    // 只转 (x,y) 会让宽高停留在世界空间（受 page.scale 影响），与静态辅助线的
    // 本地空间坐标系不一致，导致辅助线位置偏移。
    if (this.elementContainer) {
      const topLeft = this.elementContainer.toLocal(new Point(box.x, box.y));
      const bottomRight = this.elementContainer.toLocal(
        new Point(box.x + box.width, box.y + box.height)
      );

      box.x = topLeft.x;
      box.y = topLeft.y;
      box.width = bottomRight.x - topLeft.x;
      box.height = bottomRight.y - topLeft.y;
    }

    return box;
  }

  /**
   * 获取低于GUIDELINE_OFFSET中的最接近的水平垂直辅助线
   * @param lineGuideStops 所有辅助线的位置
   * @param itemBounds 目标元素的边界
   * @returns
   */
  private getGuides(
    lineGuideStops: { vertical: any; horizontal: any },
    itemBounds: { vertical: any; horizontal: any }
  ) {
    const resultV: Array<any> = [];
    const resultH: Array<any> = [];

    // 查找水平，垂直中符合要求的辅助线
    lineGuideStops.vertical.forEach((lineGuide: number) => {
      itemBounds.vertical.forEach((itemBound: { guide: number; snap: any; offset: any }) => {
        const diff = Math.abs(lineGuide - itemBound.guide);

        if (diff < this.GUIDELINE_OFFSET) {
          resultV.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset
          });
        }
      });
    });

    lineGuideStops.horizontal.forEach((lineGuide: number) => {
      itemBounds.horizontal.forEach((itemBound: { guide: number; snap: any; offset: any }) => {
        const diff = Math.abs(lineGuide - itemBound.guide);
        if (diff < this.GUIDELINE_OFFSET) {
          resultH.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset
          });
        }
      });
    });

    const guides: Array<any> = [];

    //水平，垂直结果中选取最接近的一条
    const minV = resultV.sort((a, b) => a.diff - b.diff)[0];
    const minH = resultH.sort((a, b) => a.diff - b.diff)[0];
    if (minV) {
      guides.push({
        lineGuide: minV.lineGuide,
        offset: minV.offset,
        orientation: 'V',
        snap: minV.snap
      });
    }
    if (minH) {
      guides.push({
        lineGuide: minH.lineGuide,
        offset: minH.offset,
        orientation: 'H',
        snap: minH.snap
      });
    }
    return guides;
  }

  /**
   * 根据结果画出辅助线
   * @param guides 辅助线数据
   */
  private drawGuides(guides: any[]) {
    if (!this.guideLayer) return;

    const boundary = 1000;
    const lineWidth = 3;
    const graphics = this.guideLayer;

    graphics.clear();

    // v8 必须：开启新路径上下文
    if (graphics.beginPath) graphics.beginPath();

    guides.forEach((lg: { lineGuide: number; orientation: string }) => {
      const alignedPos = Math.round(lg.lineGuide);

      if (lg.orientation === 'H') {
        // 横线
        this.drawDashedPath(graphics, -boundary, alignedPos, boundary, alignedPos);
      } else {
        // 竖线
        this.drawDashedPath(graphics, alignedPos, -boundary, alignedPos, boundary);
      }
    });
    graphics.stroke({ width: lineWidth, color: 0xff5100, cap: 'butt' });
  }

  /**
   * 通用虚线绘制工具
   * @param {Graphics} g - Pixi.Graphics 实例
   * @param {number} x1, y1 - 起点坐标
   * @param {number} x2, y2 - 终点坐标
   * @param {number} dash - 线段长度 (默认8)
   * @param {number} gap - 间隔长度 (默认5)
   */
  private drawDashedPath(
    g: Graphics,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    dash = 8,
    gap = 5
  ) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    const normalX = dx / len;
    const normalY = dy / len;

    let progress = 0;
    while (progress < len) {
      const startX = x1 + normalX * progress;
      const startY = y1 + normalY * progress;

      progress = Math.min(progress + dash, len);

      // 关键点：只 moveTo/lineTo，不在这里 stroke
      g.moveTo(startX, startY).lineTo(x1 + normalX * progress, y1 + normalY * progress);

      progress += gap;
    }
  }

  /**
   * 更新要对比的元素的label名
   * @param label
   */
  public updateCheckLabel(label: string) {
    this.checkLabel = label;
  }

  /**
   * 更新固定的辅助线位置
   * @param container
   */
  public updateStaticLineStops(container: Container | Application) {
    if (!container) return;

    let rect: Rectangle | Bounds;
    if (container instanceof Application) {
      rect = container ? container.screen : new Rectangle();
    } else {
      rect = container.getLocalBounds();
    }

    const vertical: Array<any> = [-rect.width / 2, 0, rect.width / 2];
    const horizontal: Array<any> = [-rect.height / 2, 0, rect.height / 2];

    this.staticLineStops = { vertical, horizontal };
  }

  /**
   * 按显式宽高更新固定的辅助线位置（用于 page mask 关闭后，
   * getLocalBounds() 会包含超界子元素导致边界不准的场景）
   * @param width 参考区域宽度
   * @param height 参考区域高度
   */
  public updateStaticLineStopsBySize(width: number, height: number): void {
    this.staticLineStops = {
      vertical: [-width / 2, 0, width / 2],
      horizontal: [-height / 2, 0, height / 2]
    };
  }

  // public start(...elements: Element[]) {
  public start(...elements: (Container | Sprite)[]) {
    //根据目标元素，获取所有可能出现辅助线的位置

    if (!elements || elements.length === 0) return;

    const views = elements.map((item) => item);

    if (!views || views.length === 0) return;

    const targetContainer = views[0].parent;

    if (!targetContainer) return;

    this.elementContainer = targetContainer;

    this.cachedStops = this.getLineGuideStops(views, this.checkLabel);
  }

  // public listen(...elements: Element[]) {
  public listen(...elements: (Container | Sprite)[]) {
    if (!elements || elements.length === 0 || !this.guideLayer || !this.cachedStops) return;

    const views = elements.map((item) => item);

    this.guideLayer.clear();

    if (!this.guideLayer.parent) this.elementContainer?.addChild(this.guideLayer);

    // 优化：鼠标按下时记录，不用每次移动都重新计算
    // const lineGuideStops = this.getLineGuideStops(elements, this.checkLabel);

    const box = this.getBox(views);

    //获取目标元素的区域边界
    const itemBounds = this.getObjectSnappingEdges(box);

    //获取符合要求的辅助线数据
    const guides = this.getGuides(this.cachedStops, itemBounds);

    //如果没有的听下
    if (!guides || !guides.length) return;

    this.drawGuides(guides);

    const boxCenter = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    const changePos = { x: 0, y: 0 };

    //计算粘附时，x,y改变了多少
    guides.forEach((lg) => {
      switch (lg.orientation) {
        case 'V': {
          changePos.x = lg.lineGuide + lg.offset - boxCenter.x;
          break;
        }
        case 'H': {
          changePos.y = lg.lineGuide + lg.offset - boxCenter.y;
          break;
        }
      }
    });

    views.forEach((view) => {
      const absPos = view.position;

      // 强制设置辅助线位置
      absPos.x = absPos.x + changePos.x;
      absPos.y = absPos.y + changePos.y;
      view.position.set(absPos.x, absPos.y);
    });

    if (this.onChange) this.onChange();

    // this.guideLayer.moveTo(-6000, 0).lineTo(6000, 0).stroke({ width: 3, color: 0xff0000 });
    // this.guideLayer.moveTo(0, -6000).lineTo(0, 6000).stroke({ width: 3, color: 0xff0000 });
  }

  /**
   * 移动结束，删除所有辅助线
   * @returns
   */
  public end() {
    if (!this.guideLayer) return;

    this.guideLayer.clear();
    if (this.guideLayer.parent) this.guideLayer.parent.removeChild(this.guideLayer);

    // 单独清空数据
    this.cachedStops = null;
  }

  /**
   * 清空边界数据
   */
  public clearData() {
    this.cachedStops = null;
  }

  public destroy() {
    this.end();

    if (this.guideLayer) {
      // v8 的简洁销毁方式
      // context: true 表示销毁绘图上下文（即 moveTo/lineTo 那些路径数据）
      this.guideLayer.destroy({ context: true });
    }

    this.guideLayer = null;
    this.elementContainer = null;
  }
}
